import { useRosterProfile } from '@/hooks/equip/useRosterProfile'
import { useEffectiveEmploymentStatus } from '@/hooks/equip/useEffectiveEmploymentStatus'
import { useImpersonation } from '@/context/ImpersonationContext'
import { PhysicianFAQ } from '@/pages/physician/PhysicianFAQ'
import { NonPhysicianFAQ } from '@/pages/non-physician/NonPhysicianFAQ'

/**
 * UnifiedFAQGateway
 *
 * Routes to the correct FAQ based on the user's effective roster profile.
 * Effective profile = selectedPersona (if set by admin) ?? real API result.
 *
 * Physician track  → PhysicianFAQ
 * APC track        → NonPhysicianFAQ
 * No profile match → Fallback message
 *
 * Accessible to all EQUIP_PERFORMANCE_ROLES (including equip.read).
 */
export function UnifiedFAQGateway() {
  const { data: profile, isLoading, error } = useRosterProfile()
  const { selectedPersona } = useImpersonation()
  const { effectiveEmploymentStatus } = useEffectiveEmploymentStatus()

  if (isLoading && !selectedPersona) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-eventus-teal border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-sm">Loading your FAQ...</p>
        </div>
      </div>
    )
  }

  const effectiveProfile = selectedPersona ?? profile

  if (error && !selectedPersona) {
    return <FAQFallback reason="error" />
  }

  if (!effectiveProfile || effectiveProfile.roleId === null) {
    return <FAQFallback reason="no-profile" />
  }

  if (effectiveProfile.isPhysician === true) {
    return <PhysicianFAQ employmentStatus={effectiveEmploymentStatus} />
  }

  return <NonPhysicianFAQ employmentStatus={effectiveEmploymentStatus} />
}

function FAQFallback({ reason }: { reason: 'no-profile' | 'error' }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md text-center">
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">FAQ Not Available</h2>
        <p className="text-gray-600 text-sm leading-relaxed">
          {reason === 'error'
            ? 'We were unable to load the FAQ. Please try refreshing the page or contact support.'
            : 'FAQ content is not currently available for your role. Contact your administrator if you believe this is an error.'}
        </p>
      </div>
    </div>
  )
}
